import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { setMailPassword } from '../utils/mailCredentials';
import './AtozasMailboxSetup.css';

/**
 * AtozasMailboxSetup
 *
 * Rendered at /auth/atozas/finish (the OIDC callback handoff) and
 * /auth/atozas/setup. On mount it asks the backend `GET /auth/atozas/me` to
 * determine the ATOZAS SSO session state:
 *
 *  - Not authenticated       -> bounce to /login
 *  - Authenticated + mailbox  -> reuse existing JWT, log in, go to Inbox
 *  - Authenticated, NO mailbox-> show "Create your Atozas Mail address"
 *
 * The username is validated + checked for availability server-side. Mailbox
 * creation derives the ATOZAS identity purely from the server session.
 */
const AtozasMailboxSetup = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('checking'); // checking | create | error
  const [atozas, setAtozas] = useState(null);
  const [mailDomain, setMailDomain] = useState('atozasmail.in');
  const [username, setUsername] = useState('');
  const [availability, setAvailability] = useState(null); // {valid, available, message}
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const checkTimer = useRef(null);

  const getDeviceId = () => {
    const key = 'deviceId';
    let deviceId = localStorage.getItem(key);
    if (!deviceId) {
      deviceId = `dev_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(key, deviceId);
    }
    return deviceId;
  };

  const completeLogin = useCallback((data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    if (data.mailPassword) {
      setMailPassword(data.mailPassword, data.user.email);
    }
    // Full navigation (not React Router) — IdP product-card handoff was racing
    // client-side /login ↔ /inbox redirects until a hard refresh. Reloading with
    // the JWT already in localStorage matches the path that already worked.
    const dest =
      data.returnTo && data.returnTo.startsWith('/') && !data.returnTo.startsWith('//')
        ? data.returnTo
        : '/inbox';
    window.location.replace(dest);
  }, []);

  /**
   * Create (or reuse) the ATOZAS-linked mailbox for the given username and log
   * straight in. Shared by the automatic path (server-suggested username) and
   * the manual fallback form. Returns { ok, code, message } and never throws.
   */
  const createMailbox = useCallback(
    async (usernameValue) => {
      const value = String(usernameValue || '').trim();
      if (!value) return { ok: false };
      try {
        const deviceId = getDeviceId();
        const { data } = await axiosInstance.post('/auth/atozas/mailbox', {
          username: value,
          deviceId,
        });
        if (data.success && data.data && data.data.token) {
          completeLogin(data.data);
          return { ok: true };
        }
        return { ok: false, message: data.message };
      } catch (err) {
        return {
          ok: false,
          code: err.response?.data?.code,
          message: err.response?.data?.message,
        };
      }
    },
    [completeLogin]
  );

  // Resolve the ATOZAS session on mount.
  useEffect(() => {
    // Landing here means the OIDC callback (IdP product-card launch or
    // "Continue with ATOZAS") already succeeded. Clear any prior logout /
    // JWT-expiry guard so we do not bounce to /login before /me can mint
    // a fresh app session.
    try {
      localStorage.removeItem('atozas_logged_out');
      sessionStorage.removeItem('atozas_logged_out');
    } catch (_e) {
      /* storage unavailable */
    }

    let cancelled = false;

    const fetchMe = async (deviceId) => {
      const { data } = await axiosInstance.get('/auth/atozas/me', { params: { deviceId } });
      return data;
    };

    // Session cookie from the OIDC callback redirect can lag one tick behind the
    // first XHR on some browsers after IdP product-card launch — retry briefly
    // instead of bouncing to /login (which then auto-redirects and loops).
    const fetchMeWithRetry = async (deviceId) => {
      let last = null;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        last = await fetchMe(deviceId);
        if (last && last.authenticated) return last;
        if (last && last.enabled === false) return last;
        await new Promise((r) => setTimeout(r, 150 * (attempt + 1)));
        if (cancelled) return last;
      }
      return last;
    };

    (async () => {
      try {
        const deviceId = getDeviceId();
        const data = await fetchMeWithRetry(deviceId);
        if (cancelled) return;

        if (!data || !data.enabled || !data.authenticated) {
          // Full reload so App.checkAuth can try SSO restore once more.
          window.location.replace('/login');
          return;
        }
        if (data.disabled) {
          setError(data.message || 'This account is disabled.');
          setPhase('error');
          return;
        }
        if (!data.needsMailbox && data.token) {
          completeLogin(data);
          return;
        }

        setAtozas(data.atozas || {});
        setMailDomain(data.mailDomain || 'atozasmail.in');

        // AUTOMATIC path: the backend derived a safe, available mail address.
        // Create it and go straight to the Inbox — no manual page, no refresh.
        if (data.needsMailbox && data.suggestedUsername) {
          setPhase('creating');
          const res = await createMailbox(data.suggestedUsername);
          if (cancelled) return;
          if (res.ok) return; // logged in + navigated to Inbox

          // A mailbox already exists for this ATOZAS identity -> re-resolve to
          // pick up its existing JWT and log in.
          if (res.code === 'ALREADY_LINKED') {
            try {
              const again = await fetchMe(getDeviceId());
              if (cancelled) return;
              if (again && !again.needsMailbox && again.token) {
                completeLogin(again);
                return;
              }
            } catch (_e) {
              /* fall through to the manual form */
            }
          }

          // Race (name just taken) or unexpected failure -> manual fallback,
          // prefilled with the suggestion so the user can pick another in one tap.
          setUsername(data.suggestedUsername);
          setError(res.message || '');
          setPhase('create');
          return;
        }

        // FALLBACK: no username could be safely derived -> manual selection.
        setPhase('create');
      } catch (err) {
        if (cancelled) return;
        setError('Could not verify your ATOZAS session. Please try again.');
        setPhase('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [completeLogin, createMailbox]);

  // Debounced availability check.
  useEffect(() => {
    if (phase !== 'create') return undefined;
    if (checkTimer.current) clearTimeout(checkTimer.current);

    const value = username.trim();
    if (!value) {
      setAvailability(null);
      setChecking(false);
      return undefined;
    }

    setChecking(true);
    checkTimer.current = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.post('/auth/atozas/username-check', {
          username: value,
        });
        setAvailability({
          valid: !!data.valid,
          available: !!data.available,
          message: data.message || '',
        });
      } catch (err) {
        setAvailability({ valid: false, available: false, message: 'Could not check availability.' });
      } finally {
        setChecking(false);
      }
    }, 400);

    return () => {
      if (checkTimer.current) clearTimeout(checkTimer.current);
    };
  }, [username, phase]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const value = username.trim();
    if (!value || !availability || !availability.valid || !availability.available) return;

    setSubmitting(true);
    const res = await createMailbox(value);
    if (!res.ok) {
      setError(res.message || 'Could not create your mailbox. Please try again.');
    }
    setSubmitting(false);
  };

  if (phase === 'checking' || phase === 'creating') {
    return (
      <div className="atozas-setup-container">
        <div className="atozas-setup-card">
          <div className="atozas-setup-spinner" />
          <p>{phase === 'creating' ? 'Setting up your inbox…' : 'Connecting to ATOZAS…'}</p>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="atozas-setup-container">
        <div className="atozas-setup-card">
          <img className="atozas-setup-logo" src="/atozasmail-logo.png" alt="ATOZASMail" />
          <h2>Something went wrong</h2>
          <p className="atozas-setup-error">{error}</p>
          <button className="atozas-setup-submit" onClick={() => navigate('/login', { replace: true })}>
            Back to login
          </button>
        </div>
      </div>
    );
  }

  const previewEmail = username.trim()
    ? `${username.trim().toLowerCase()}@${mailDomain}`
    : `username@${mailDomain}`;

  const canSubmit =
    !submitting && availability && availability.valid && availability.available && !checking;

  return (
    <div className="atozas-setup-container">
      <div className="atozas-setup-card">
        <img className="atozas-setup-logo" src="/atozasmail-logo.png" alt="ATOZASMail" />
        <h2>Create your Atozas Mail address</h2>
        <p className="atozas-setup-subtitle">
          Signed in as <strong>{atozas?.email}</strong> via ATOZAS. Choose your new mail address to
          finish setting up your inbox.
        </p>

        <form onSubmit={handleSubmit} className="atozas-setup-form">
          <label htmlFor="atozas-username">Choose a username</label>
          <div className="atozas-username-row">
            <input
              id="atozas-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="username"
              autoComplete="off"
              autoFocus
              maxLength={30}
              disabled={submitting}
            />
            <span className="atozas-domain">@{mailDomain}</span>
          </div>

          <div className="atozas-preview">
            Your address: <strong>{previewEmail}</strong>
          </div>

          {username.trim() && (
            <div
              className={`atozas-availability ${
                checking
                  ? 'is-checking'
                  : availability && availability.valid && availability.available
                  ? 'is-ok'
                  : 'is-bad'
              }`}
            >
              {checking
                ? 'Checking availability…'
                : availability
                ? availability.message
                : ''}
            </div>
          )}

          {error && <div className="atozas-setup-error">{error}</div>}

          <button type="submit" className="atozas-setup-submit" disabled={!canSubmit}>
            {submitting ? 'Creating…' : 'Create Mail Account'}
          </button>
        </form>

        <p className="atozas-setup-hint">
          You won’t need a separate password — ATOZAS keeps you signed in.
        </p>
      </div>
    </div>
  );
};

export default AtozasMailboxSetup;
